import { useQuery } from '@tanstack/react-query'
import { useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useQueryState } from '@/hooks/use-query-state'
import { EMPTY_VALUE, formatDateTime } from '@/lib/format'
import { actionStatusTone, isOpenAction } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { hrApi, type HrIntervention } from '@/services/http/hr'
import { RequestStatusBadge } from '@/features/hr/ApprovalsPage'
import { HrPageHeader } from '@/features/hr/HrPageHeader'
import { actionLabel } from '@/features/hr/hr-actions'
import { DetailGrid, HistoryTimeline, PersonCell } from '@/features/hr/parts'
import { ExportButton } from '@/components/data-table/ExportButton'
import { interventionsExport } from '@/features/hr/exports'

const t = messages.hr
const m = t.interventions
const TABS = ['all', 'talep', 'aksiyon', 'surec'] as const
type Tab = (typeof TABS)[number]

function tabOf(item: HrIntervention): Exclude<Tab, 'all'> {
  if (item.source === 'talep') return 'talep'
  return item.target_kind === 'surec' ? 'surec' : 'aksiyon'
}

/** Talep `giris_cikis_ekle` → onaydan doğan aksiyonun adı ("DİA’ya giriş/çıkış ekle"). */
function interventionLabel(item: HrIntervention): string {
  return actionLabel(item.source === 'talep' ? `talep_${item.type}` : item.type)
}

function isOpen(item: HrIntervention): boolean {
  return (
    item.request_status === 'onay_bekliyor' ||
    item.request_status === 'revizyonda' ||
    isOpenAction(item.action_status ? { status: item.action_status } : undefined)
  )
}

function matches(item: HrIntervention, needle: string): boolean {
  return [
    item.personel_adi,
    item.sicilno,
    item.firma,
    item.sube,
    item.requested_by.name,
    item.decision?.actor.name,
    item.target_id,
    interventionLabel(item),
  ].some((value) => value?.toLocaleLowerCase('tr').includes(needle))
}

function TargetCell({ item }: { item: HrIntervention }) {
  if (item.target_kind === 'surec') {
    return <span>{t.processes.names[item.target_id] ?? item.target_id}</span>
  }
  if (item.personel_adi || item.sicilno) {
    return <PersonCell name={item.personel_adi} sub={item.sicilno} />
  }
  return (
    <span className="flex flex-col leading-tight">
      {m.target[item.target_kind] ?? item.target_kind}
      <span className="text-text-muted text-xs">{item.target_id}</span>
    </span>
  )
}

function ActorLine({ name, at }: { name: string | null; at: string }) {
  return (
    <span className="text-text-muted text-xs">
      {name ?? t.unknown} · {formatDateTime(at)}
    </span>
  )
}

/** Talepte durum + son kararı veren; doğrudan aksiyonda onay adımı yoktur. */
function ApprovalCell({ item }: { item: HrIntervention }) {
  if (!item.request_status) return <span className="text-text-muted text-xs">{m.noApproval}</span>
  return (
    <span className="flex flex-col items-start gap-0.5">
      {item.direct ? (
        <Badge tone="warning" size="sm">
          {m.direct}
        </Badge>
      ) : (
        <RequestStatusBadge status={item.request_status} />
      )}
      {item.decision && <ActorLine name={item.decision.actor.name} at={item.decision.at} />}
    </span>
  )
}

function ResultCell({ item }: { item: HrIntervention }) {
  if (!item.action_status) {
    return (
      <span className="text-text-muted text-xs">
        {item.request_status === 'iptal' ? m.notSent : m.awaiting}
      </span>
    )
  }
  return (
    <span className="flex flex-col items-start gap-0.5">
      <Badge tone={actionStatusTone(item.action_status)} size="sm">
        {t.actionStatus[item.action_status] ?? item.action_status}
      </Badge>
      {item.finished_at && (
        <span className="text-text-muted text-xs tabular-nums">
          {formatDateTime(item.finished_at)}
        </span>
      )}
    </span>
  )
}

function InterventionSheet({
  item,
  onClose,
}: {
  item: HrIntervention | undefined
  onClose: () => void
}) {
  return (
    <Sheet open={item !== undefined} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{item ? interventionLabel(item) : m.title}</SheetTitle>
          <SheetDescription>
            {item && (item.personel_adi ?? m.target[item.target_kind] ?? item.target_kind)}
          </SheetDescription>
        </SheetHeader>
        {item && (
          <SheetBody className="flex flex-col gap-4">
            <DetailGrid
              items={[
                [m.columns.mudahale, `${m.source[item.source]} · ${interventionLabel(item)}`],
                [m.record, <TargetCell key="k" item={item} />],
                [m.columns.firma, [item.firma, item.sube].filter(Boolean).join(' · ') || null],
                [
                  m.requestedBy,
                  <ActorLine key="r" name={item.requested_by.name} at={item.requested_at} />,
                ],
                [m.columns.onay, <ApprovalCell key="o" item={item} />],
                [
                  m.columns.sonuc,
                  <span key="s" className="flex flex-col items-start gap-1">
                    <ResultCell item={item} />
                    {item.action_message && (
                      <span className="text-text-secondary text-xs whitespace-pre-wrap">
                        {item.action_message}
                      </span>
                    )}
                  </span>,
                ],
              ]}
            />
            {item.source === 'talep' && (
              <Button
                size="sm"
                variant="secondary"
                className="w-fit"
                nativeButton={false}
                render={<Link to={`/ik/onaylar?talep=${item.id}`} />}
              >
                {m.openRequest}
              </Button>
            )}
            <section className="border-border flex flex-col gap-2 border-t pt-4">
              <h3 className="text-sm font-medium">{t.detail.history}</h3>
              <div className="[--timeline-surface:var(--surface)]">
                <HistoryTimeline events={item.events} />
              </div>
            </section>
          </SheetBody>
        )}
      </SheetContent>
    </Sheet>
  )
}

export function InterventionsPage() {
  const query = useQueryState()
  const tab = (TABS as readonly string[]).includes(query.get('sekme') ?? '')
    ? (query.get('sekme') as Tab)
    : 'all'
  const q = query.get('q') ?? ''
  const selectedId = query.get('mudahale')
  const list = useQuery({
    queryKey: queryKeys.hr.interventions(),
    queryFn: hrApi.interventions,
    // Açık talep/aksiyon varken sonucu kısa aralıkla izle.
    refetchInterval: (state) => (state.state.data?.some(isOpen) ? 5000 : 30_000),
  })
  const items = useMemo(() => list.data ?? [], [list.data])
  const counts = useMemo(() => {
    const result: Record<Tab, number> = { all: items.length, talep: 0, aksiyon: 0, surec: 0 }
    for (const item of items) result[tabOf(item)] += 1
    return result
  }, [items])
  const rows = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('tr')
    return items.filter(
      (item) => (tab === 'all' || tabOf(item) === tab) && (!needle || matches(item, needle)),
    )
  }, [items, tab, q])
  const selected = items.find((item) => item.id === selectedId)

  const cells: [string, (item: HrIntervention) => ReactNode][] = [
    [
      m.columns.zaman,
      (item) => <span className="tabular-nums">{formatDateTime(item.requested_at)}</span>,
    ],
    [
      m.columns.mudahale,
      (item) => (
        <span className="flex flex-col items-start gap-0.5">
          {interventionLabel(item)}
          <span className="text-text-muted text-xs">{m.source[item.source]}</span>
        </span>
      ),
    ],
    [m.columns.kayit, (item) => <TargetCell item={item} />],
    [m.columns.firma, (item) => item.firma ?? EMPTY_VALUE],
    [
      m.columns.yapan,
      (item) => (
        <span className="flex items-center gap-2">
          <Avatar name={item.requested_by.name ?? '?'} size="sm" />
          {item.requested_by.name ?? t.unknown}
        </span>
      ),
    ],
    [m.columns.onay, (item) => <ApprovalCell item={item} />],
    [m.columns.sonuc, (item) => <ResultCell item={item} />],
  ]

  return (
    <div className="flex flex-col gap-4">
      <HrPageHeader
        title={m.title}
        description={m.description}
        actions={<ExportButton filename="ik-mudahaleler" {...interventionsExport(rows)} />}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={tab}
          onValueChange={(value: Tab) => query.set({ sekme: value === 'all' ? undefined : value })}
        >
          <TabsList>
            {TABS.map((item) => (
              <TabsTrigger key={item} value={item}>
                {m.tabs[item]}
                <span className="text-text-muted tabular-nums">{counts[item]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Input
          key={q}
          aria-label={t.filters.search}
          className="w-64"
          placeholder={m.searchPlaceholder}
          defaultValue={q}
          onBlur={(event) => query.set({ q: event.target.value || undefined })}
          onKeyDown={(event) => {
            if (event.key === 'Enter') query.set({ q: event.currentTarget.value || undefined })
          }}
        />
      </div>
      {list.isLoading ? (
        <Loading rows={5} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <Empty hint={items.length === 0 ? m.empty : m.emptyFiltered} />
      ) : (
        <>
          <p className="text-text-muted text-xs">{m.count(rows.length, items.length)}</p>
          <div className="border-border rounded-panel bg-surface overflow-x-auto border">
            <Table>
              <TableHeader>
                <TableRow>
                  {cells.map(([label]) => (
                    <TableHead key={label}>{label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((item) => (
                  <TableRow
                    key={item.id}
                    tabIndex={0}
                    className="cursor-pointer"
                    onClick={() => query.set({ mudahale: item.id })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') query.set({ mudahale: item.id })
                    }}
                  >
                    {cells.map(([label, cell]) => (
                      <TableCell key={label}>{cell(item)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      <InterventionSheet item={selected} onClose={() => query.set({ mudahale: undefined })} />
    </div>
  )
}
